import { Drawer as MuiDrawer } from '@material-ui/core';
import { ReactNode, useState } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled, { css } from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { BackButton } from '@sorare/core/src/components/dialog/BackButton';
import { CloseButton } from '@sorare/core/src/components/dialog/CloseButton';
import {
  AlgoliaCardIndexesName,
  PrintablePlayerSortName,
} from '@sorare/core/src/contexts/config';
import { FilterWidget, FiltersGroup } from '@sorare/core/src/lib/filters';
import { filters } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { OverrideClasses, regularMaterial } from '@sorare/core/src/style/utils';

import { useClearAllFilters } from 'search/ClearAllFilters';
import useFiltersCount from 'search/FiltersManager/useFiltersCount';

import { MobileFilters } from './MobileFilters';
import { SortWidget } from './SortWidget';

type Props = {
  sorts?: (AlgoliaCardIndexesName | PrintablePlayerSortName)[];
  open: boolean;
  onClose: () => void;
  filtersGroups: FiltersGroup[];
  children?: ReactNode;
};

const messages = defineMessages({
  title: {
    id: 'FiltersManagersDialog.title',
    defaultMessage: 'Filters',
  },
});

const Body = styled(Vertical).attrs({ gap: 2 })`
  width: 100%;
  min-height: 100%;
  background-color: var(--c-black);
  padding: var(--triple-unit) var(--double-unit) 0;
  @media ${tabletAndAbove} {
    width: 400px;
  }
`;

const Header = styled(Horizontal)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--unit);
  position: sticky;
  bottom: 0;
  padding: var(--double-unit) 0 var(--triple-unit);
  background-color: var(--c-black);
  @media ${tabletAndAbove} {
    flex-direction: row;
  }
`;

const [Drawer, classes] = OverrideClasses(MuiDrawer, null, {
  paper: css`
    width: 100%;
    ${regularMaterial}
    @media ${tabletAndAbove} {
      width: 400px;
    }
  `,
});

export const FiltersManagerDialog = ({
  sorts,
  open,
  onClose: onCloseDialog,
  filtersGroups,
  children,
}: Props) => {
  const [activeWidget, setActiveWidget] = useState<FilterWidget>();
  const clearAllFilters = useClearAllFilters();
  const filtersCount = useFiltersCount();
  const { results } = useInstantSearch();

  const groups = [
    ...((sorts && [
      {
        slug: 'sort',
        filters: [SortWidget({ sorts })],
      },
    ]) ||
      []),
    ...(filtersGroups || []),
  ];

  const onClose = () => {
    setActiveWidget(undefined);
    onCloseDialog();
  };

  return (
    <Drawer
      open={open}
      anchor="right"
      classes={{ paper: classes.paper }}
      onClose={() => (activeWidget ? setActiveWidget(undefined) : onClose())}
      keepMounted
    >
      <Body>
        <Header>
          <BackButton
            onBack={() =>
              activeWidget ? setActiveWidget(undefined) : onClose()
            }
          />
          {activeWidget ? (
            activeWidget.title
          ) : (
            <BodyM bold>
              <FormattedMessage {...messages.title} />
            </BodyM>
          )}
          <CloseButton onClose={onClose} />
        </Header>

        {children}
        <MobileFilters
          filters={groups}
          onClick={(widget: FilterWidget) => setActiveWidget(widget)}
          activeWidget={activeWidget}
        />
        <ButtonsWrapper>
          <Button
            color="red"
            size="medium"
            onClick={clearAllFilters}
            disabled={!filtersCount}
            fullWidth
          >
            <BodyM>
              <FormattedMessage {...filters.clearAll} />
            </BodyM>
          </Button>
          <Button onClick={onClose} size="medium" color="primary" fullWidth>
            <FormattedMessage
              id="FiltersManagerDialog.viewResults"
              defaultMessage="Show {results, number} results"
              values={{
                results: results ? results.nbHits : 0,
              }}
            />
          </Button>
        </ButtonsWrapper>
      </Body>
    </Drawer>
  );
};

export default FiltersManagerDialog;
